<!--
+------------------------------------------------------------------+
| Author: Jeffrey Thümann                                          |
| Component: FamilyTree                                            |
| Section: views/trees                                             |
| Purpose: Family tree route for broad lineage navigation.         |
+------------------------------------------------------------------+
-->
<template>
  <div class="generation-table">
    <!-- 0) LOOP over every row in rows -->
    <div v-for="(cells, rowIndex) in rows" :key="rowIndex" :class="`gen-level-${generations - rowIndex}`"
      :style="{ display: cells.every(cell => !cell) ? 'none' : undefined }">

      <!-- 1) the row of person‑cells -->
      <div class="generation-row">
        <div v-for="(person, cellIndex) in cells" :key="cellIndex" class="generation-cell"
          :style="{ opacity: person ? 1 : 0 }">
          <a v-if="person && person.personID" :href="personLink(person.personID)"
            style="color: inherit; text-decoration: inherit;">
            {{ person.firstname }} {{ person.lastname }}<br>
            <span>{{ getFormattedYears(person) }}</span>
          </a>
          <span v-else-if="person"> {{ person.firstname }} {{ person.lastname }} </span>
          <span v-else>{{ $t('treeViews.unknown') }}</span>
        </div>
      </div>

      <!-- 2) ONLY if there’s another row below -->
      <div v-if="rowIndex < generations" class="generation-dividers" :id="`gen-${generations - rowIndex}`">
        <!-- 2a) vertical ABOVE -->
        <div class="generation-divider--vertical">
          <div v-for="(show, idx) in dividerStates[rowIndex].verticalAbove" :key="'va-' + idx"
            class="generation-divider" :style="{ opacity: show ? 1 : 0 }"></div>
        </div>

        <!-- 2b) horizontals (full or half) -->
        <div class="generation-divider--horizontal">
          <div v-for="(status, idx) in dividerStates[rowIndex].horizontals" :key="idx" class="generation-divider"
            :class="{
              'half-left': status === 'left',
              'half-right': status === 'right'
            }" :style="{ opacity: status === 'none' ? 0 : 1 }"></div>
        </div>

        <!-- 2c) vertical BELOW -->
        <div class="generation-divider--vertical">
          <div v-for="(show, idx) in dividerStates[rowIndex].verticalBelow" :key="'vb-' + idx"
            class="generation-divider" :style="{ opacity: show ? 1 : 0 }"></div>
        </div>
      </div>

    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { formatYears } from '@/utils/dateUtils.js';

const api = axios.create({
  baseURL: process.env.VUE_APP_BACKEND_API_URL,
  headers: {
    "x-api-key": process.env.VUE_APP_API_KEY,
  },
});

export default {
  name: 'FamilyTree',
  props: {
    personID: {
      type: String,
      required: true
    },
    generations: {
      type: Number,
      default: 3
    }
  },
  data() {
    return {
      rootPerson: null
    };
  },
  computed: {
    // Build an array of rows, each row = array of person objects (or null)
    rows() {
      if (!this.rootPerson) return [];

      // Helper: given a path of bits [0,1,0,…], walk down the nested ancestors
      const getByPath = (node, path) => {
        return path.reduce((curr, idx) => {
          return curr && curr.ancestors
            ? curr.ancestors[idx] || null
            : null;
        }, node);
      };

      const allRows = [];

      // for each generation above root
      for (let level = this.generations; level >= 1; level--) {
        const count = 2 ** level;
        const row = [];

        for (let i = 0; i < count; i++) {
          // binary bits of i, length = level
          const path = [];
          for (let bit = level - 1; bit >= 0; bit--) {
            path.push((i >> bit) & 1);
          }
          row.push(getByPath(this.rootPerson, path));
        }
        allRows.push(row);
      }

      // finally the root itself
      allRows.push([this.rootPerson]);

      return allRows;
    },
    dividerStates() {
      return this.rows
        .slice(0, this.generations)
        .map((cells, rowIndex) => {
          const childCells = this.rows[rowIndex + 1] || [];

          // 1) bars above each parent
          const verticalAbove = cells.map(p => Boolean(p));

          // 2) for each PAIR of parents, decide status
          const horizontals = [];
          for (let i = 0; i < cells.length; i += 2) {
            const left = cells[i];
            const right = cells[i + 1];
            const kid = childCells[i / 2];
            if (left && right && kid) {
              horizontals.push('both');
            } else if (left && kid) {
              horizontals.push('left');
            } else if (right && kid) {
              horizontals.push('right');
            } else {
              horizontals.push('none');
            }
          }

          // 3) bars below each child: show whenever status ≠ 'none'
          const verticalBelow = horizontals.map(s => s !== 'none');

          return { verticalAbove, horizontals, verticalBelow };
        });
    }
  },
  mounted() {
    this.fetchAncestors();
  },
  methods: {
    personLink(personID) {
      const locale = this.$route?.params?.locale;
      const path = `/person/${personID}#parentSection`;
      return locale ? `/${locale}${path}` : path;
    },
    async fetchAncestors() {
      try {
        const { data } = await api.get(
          `/ancestors/${this.personID}`,
          { params: { generations: this.generations } }
        );
        this.rootPerson = data;
      } catch (e) {
        console.error('Failed loading ancestors', e);
      }
    },
    getFormattedYears(person) {
      if (!person) return '';
      // Use the same fields as in ParentSection.vue
      return formatYears(
        person.birthdatetr, // or person.birthdate if that's what your API returns
        person.altbirthdate,
        person.deathdatetr, // or person.deathdate
        person.burialdatetr, // or person.burialdate
        person.living
      );
    },
  }
};
</script>

<style scoped>
.generation-table {
  display: flex;
  flex-direction: column;
}

.generation-row {
  display: flex;
  flex-direction: row;
}

.generation-cell {
  display: flex;
  flex: 1 0 0;
  align-content: center;
  align-items: center;
  justify-content: center;
  color: var(--brand-text-light);
  padding: 8px 4px 12px 4px;
}

.generation-dividers {
  display: flex;
  flex-direction: column;
}

.generation-divider--vertical,
.generation-divider--horizontal {
  display: flex;
  flex-direction: row;
  justify-content: space-around;
  width: 100%;
}

.generation-divider--horizontal {
  justify-content: space-around;
}

.generation-divider--vertical .generation-divider {
  height: 32px;
  width: 2px;
  background-color: white;
}

.generation-divider--vertical .generation-divider:nth-child(odd) {
  opacity: 0;
}

.generation-divider--horizontal .generation-divider {
  height: 2px;
  background-color: white;
}

/* if you still want the fixed‑width selectors for 3 gens, you can keep these: */
#gen-3 .generation-divider--horizontal .generation-divider {
  width: 12.5%;
}

#gen-2 .generation-divider--horizontal .generation-divider {
  width: 25%;
}

#gen-1 .generation-divider--horizontal .generation-divider {
  width: 50%;
}

/* make sure your .generation-divider is positioning‑capable */
.generation-divider {
  position: relative;
}

/* mother unknown → only father’s half shows,
   so cover the left half in pink */
.generation-divider.half-left::before {
  content: '';
  position: absolute;
  top: 0;
  right: 0;
  width: 50%;
  height: 100%;
  background: var(--brand-bg-3);
  /* your pink */
}

/* father unknown → only mother’s half shows,
   so cover the right half in pink */
.generation-divider.half-right::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 50%;
  height: 100%;
  background: var(--brand-bg-3);
}

.generation-cell a {
  font-weight: 600;
  text-align: center;
  display: flex;
  flex-direction: column;
  gap: 0;
  line-height: normal;
}

.generation-cell a span {
  font-size: 10px;
  font-weight: 400;
  line-height: normal;
}

.generation-cell a:hover {
  text-decoration: underline !important;
}

/* probandus (root) */
.gen-level-0 .generation-cell a {
  font-size: 24px;
}

/* parents */
.gen-level-1 .generation-cell a {
  font-size: 20px;
}

/* grandparents */
.gen-level-2 .generation-cell a {
  font-size: 18px;
}

/* great‑grandparents */
.gen-level-3 .generation-cell a {
  font-size: 14px;
}

/* …or remove them and compute widths inline via :style */
</style>
